import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ScoreState } from './types';
import {
  Score,
  ScoreAnswer,
  ScoresAnswersListRequest,
  ScoresListRequest,
  ScoresToggleIsCorrectUpdateRequest
} from '../../../generated/api';
import { getScoresApiClient } from '../../../common/apiClients';
import { getMessageFromError } from '../../../common/utils/apiUtils';
import { RootState } from '../../../app/store';
import { saveAs } from 'file-saver';

const sliceName = 'scores';

const initialState: ScoreState = {
  scores: [],
  loading: false
};

export const fetchScoresAsync = createAsyncThunk<Score[], string>(
  `${sliceName}/fetchScores`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getScoresApiClient();
      const request: ScoresListRequest = { attemptId: arg };
      return await apiClient.scoresList(request);
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const fetchScoresCSVAsync = createAsyncThunk<void, { id: string; fileName: string }>(
  `${sliceName}/fetchScoresCSV`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getScoresApiClient();
      const request: ScoresListRequest = { attemptId: arg.id };
      const response = await apiClient.scoresCsvRetrieve(request);
      saveAs(response, arg.fileName);
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const fetchScoreAnswersAsync = createAsyncThunk<ScoreAnswer[], ScoresAnswersListRequest>(
  `${sliceName}/fetchScoreAnswers`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getScoresApiClient();
      return await apiClient.scoresAnswersList(arg);
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

export const toggleIsCorrectAsync = createAsyncThunk<void, ScoresToggleIsCorrectUpdateRequest>(
  `${sliceName}/toggleIsCorrect`,
  async (arg, { rejectWithValue }) => {
    try {
      const apiClient = await getScoresApiClient();
      await apiClient.scoresToggleIsCorrectUpdate(arg);
    } catch (e) {
      return rejectWithValue(await getMessageFromError(e));
    }
  }
);

const scoreSlice = createSlice({
  name: sliceName,
  initialState: initialState,
  reducers: {
    clearScore: (state): ScoreState => {
      return { ...state, scores: [] };
    }
  },
  extraReducers: {
    [fetchScoresAsync.pending.type]: (state): ScoreState => {
      return { ...state, loading: true };
    },
    [fetchScoresAsync.rejected.type]: (state): ScoreState => {
      return { ...state, scores: [], loading: false };
    },
    [fetchScoresAsync.fulfilled.type]: (state, action: PayloadAction<Score[]>): ScoreState => {
      return { ...state, scores: action.payload, loading: false };
    }
  }
});

export const selectScores = (state: RootState): ScoreState => state.scores;
export const { clearScore } = scoreSlice.actions;
export default scoreSlice.reducer;
